import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Input } from "./input";

export type AssociationFormValues = {
  name: string;
  domain: string;
  organizationNumber: string;
};

export function AssociationForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
  requireAllFields = true,
  submitLabel,
}: {
  onCancel: () => void;
  onChange: (values: AssociationFormValues) => void;
  onSubmit: () => void;
  values: AssociationFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  requireAllFields?: boolean;
  submitLabel: string;
}) {
  const { locale } = useLocale();

  const isValid = useMemo(() => {
    if (!requireAllFields) {
      return (
        values.name.trim() !== "" ||
        values.domain.trim() !== "" ||
        values.organizationNumber.trim() !== ""
      );
    }

    return (
      values.name.trim() !== "" &&
      values.domain.trim() !== "" &&
      values.organizationNumber.trim() !== ""
    );
  }, [values, requireAllFields]);

  const isDisabled = disabled || loading;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Input
        disabled={isDisabled}
        hasError={hasError}
        label={locale.auth.associationName}
        required={requireAllFields}
        type="text"
        value={values.name}
        onChange={(e) => onChange({ ...values, name: e.target.value })}
      />
      <Input
        disabled={isDisabled}
        hasError={hasError}
        label={locale.auth.associationDomain}
        required={requireAllFields}
        type="text"
        value={values.domain}
        onChange={(e) => onChange({ ...values, domain: e.target.value })}
      />
      <Input
        disabled={isDisabled}
        hasError={hasError}
        label={locale.auth.organizationNumber}
        required={requireAllFields}
        type="text"
        value={values.organizationNumber}
        onChange={(e) =>
          onChange({ ...values, organizationNumber: e.target.value })
        }
      />
      <div className="flex items-center justify-end gap-2">
        {hasError && (
          <p className="flex items-center gap-2 text-red-500">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {locale.auth.associationFormError}
          </p>
        )}
        <Button
          disabled={isDisabled}
          severity="secondary"
          type="button"
          onClick={onCancel}
        >
          {locale.page.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
